/**
 * Entity field definitions for workflow automation
 * Defines available fields for each entity type that can be used in conditions and actions
 */

export interface EntityField {
  name: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'select';
  options?: Array<{ value: any; label: string }>;
  // For foreign key fields (number type that references another table)
  apiEndpoint?: string; // API endpoint to fetch related entities
  labelField?: string; // Field name in API response to use as label
  valueField?: string; // Field name in API response to use as value (default: id or field-specific)
}

export const entityFields: Record<string, EntityField[]> = {
  Task: [
    { name: 'taskDescription', label: 'Description', type: 'string' },
    { name: 'taskStatus', label: 'Status', type: 'select', options: [
      { value: 1, label: 'Not Started' },
      { value: 2, label: 'In Progress' },
      { value: 3, label: 'Awaiting Feedback' },
      { value: 4, label: 'Testing' },
      { value: 5, label: 'Completed' },
    ]},
    { name: 'taskPriority', label: 'Priority', type: 'select' },
    { name: 'taskAssignedTo', label: 'Assigned To', type: 'number', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
    { name: 'taskAssignedFrom', label: 'Assigned From', type: 'number', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
    { name: 'taskDate', label: 'Task Date', type: 'date' },
    { name: 'taskDueDate', label: 'Due Date', type: 'date' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
    { name: 'projectId', label: 'Project', type: 'number', apiEndpoint: '/projects', labelField: 'projectName', valueField: 'id' },
    { name: 'supportTicketId', label: 'Support Ticket', type: 'number', apiEndpoint: '/support-ticket', labelField: 'title', valueField: 'id' },
  ],
  Ticket: [
    { name: 'title', label: 'Title', type: 'string' },
    { name: 'description', label: 'Description', type: 'string' },
    { name: 'status', label: 'Status', type: 'select' },
    { name: 'priority', label: 'Priority', type: 'select' },
    { name: 'assignedTo', label: 'Assigned To', type: 'number', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
    { name: 'createdAt', label: 'Created At', type: 'date' },
    { name: 'updatedAt', label: 'Updated At', type: 'date' },
  ],
  Client: [
    { name: 'clientName', label: 'Client Name', type: 'string' },
    { name: 'clientName2', label: 'Client Name 2', type: 'string' },
    { name: 'clientCode', label: 'Client Code', type: 'string' },
    { name: 'clientEmail', label: 'Email', type: 'string' },
    { name: 'clientPhone', label: 'Phone', type: 'string' },
    { name: 'clientIsActive', label: 'Is Active', type: 'boolean' },
    { name: 'isClient', label: 'Is Client', type: 'boolean' },
    { name: 'contactPerson', label: 'Contact Person', type: 'string' },
  ],
  Lead: [
    { name: 'leadName', label: 'Lead Name', type: 'string' },
    { name: 'leadEmail', label: 'Email', type: 'string' },
    { name: 'leadPhone', label: 'Phone', type: 'string' },
    { name: 'leadStatus', label: 'Status', type: 'select' },
    { name: 'leadAssignedTo', label: 'Assigned To', type: 'number', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
    { name: 'leadSource', label: 'Source', type: 'string' },
  ],
  Invoice: [
    { name: 'invoiceNumber', label: 'Invoice Number', type: 'string' },
    { name: 'invoiceAmount', label: 'Amount', type: 'number' },
    { name: 'invoiceIncome', label: 'Income', type: 'number' },
    { name: 'invoiceDate', label: 'Invoice Date', type: 'date' },
    { name: 'invoiceDueDate', label: 'Due Date', type: 'date' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
    { name: 'invoiceStatus', label: 'Status', type: 'select' },
  ],
  Note: [
    { name: 'noteTitle', label: 'Title', type: 'string' },
    { name: 'noteContent', label: 'Content', type: 'string' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
    { name: 'createdAt', label: 'Created At', type: 'date' },
  ],
  Todo: [
    { name: 'todoDescr', label: 'Description', type: 'string' },
    { name: 'todoCompleted', label: 'Completed', type: 'boolean' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
    { name: 'todoCreatedAt', label: 'Created At', type: 'date' },
  ],
  Project: [
    { name: 'projectName', label: 'Project Name', type: 'string' },
    { name: 'projectDescription', label: 'Description', type: 'string' },
    { name: 'projectStatus', label: 'Status', type: 'select' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
    { name: 'startDate', label: 'Start Date', type: 'date' },
    { name: 'endDate', label: 'End Date', type: 'date' },
  ],
  ClientContact: [
    { name: 'firstName', label: 'First Name', type: 'string' },
    { name: 'lastName', label: 'Last Name', type: 'string' },
    { name: 'email', label: 'Email', type: 'string' },
    { name: 'phone', label: 'Phone', type: 'string' },
    { name: 'position', label: 'Position', type: 'string' },
    { name: 'clientId', label: 'Client', type: 'number', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
  ],
  Email: [
    { name: 'subject', label: 'Subject', type: 'string' },
    { name: 'from', label: 'From', type: 'string' },
    { name: 'fromDomain', label: 'Sender domain', type: 'string' },
    { name: 'to', label: 'To', type: 'string' },
    { name: 'cc', label: 'Cc', type: 'string' },
    { name: 'toOrCc', label: 'To or Cc', type: 'string' },
    { name: 'fromToCcBcc', label: 'From, To, Cc or Bcc', type: 'string' },
    { name: 'date', label: 'Date', type: 'date' },
    { name: 'receivedDate', label: 'Received date', type: 'date' },
    { name: 'folder', label: 'Folder', type: 'string' },
    { name: 'hasAttachments', label: 'Has attachments', type: 'boolean' },
    { name: 'attachmentCount', label: 'Attachment count', type: 'number' },
    { name: 'ageInDays', label: 'Age in days', type: 'number' },
    { name: 'totalSizeKB', label: 'Size (KB)', type: 'number' },
  ],
};

export const entityTypes = [
  { value: 'Task', label: 'Task' },
  { value: 'Ticket', label: 'Support Ticket' },
  { value: 'Client', label: 'Client' },
  { value: 'Lead', label: 'Lead' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Note', label: 'Note' },
  { value: 'Todo', label: 'Todo' },
  { value: 'Project', label: 'Project' },
  { value: 'ClientContact', label: 'Client Contact' },
  { value: 'Email', label: 'Email' },
];

export const getFieldsForEntity = (entityType: string): EntityField[] => {
  return entityFields[entityType] || [];
};

export const getFieldByName = (entityType: string, fieldName: string): EntityField | undefined => {
  const fields = getFieldsForEntity(entityType);
  return fields.find(f => f.name === fieldName);
};
